import { memo, useMemo } from "react";
import Html from "react-pdf-html";

import * as cheerio from "cheerio";

import PdfDocumentTable, {
  buildTableDescriptionFromHtml
} from "components/pdf-generation-v3/components/pdf-document-html/pdf-document-table.component";
import {
  DEFAULT_PDF_CSS_STYLE,
  normalizePrintableHtml
} from "components/pdf-generation-v3/pdf-generation-v3.utils";

type PdfDocumentHtmlProps = {
  bodyHtml?: string | null;
};

type HtmlChunk = { kind: "table" | "html"; html: string };

// Pre-compute static HTML wrapper parts for better performance
const WRAP_PREFIX = `<html><body><style>`;
const WRAP_MID = `</style>`;
const WRAP_SUFFIX = `</body></html>`;
const DEFAULT_STYLE = DEFAULT_PDF_CSS_STYLE;
const STATIC_WRAPPER_START = `${WRAP_PREFIX}${DEFAULT_STYLE}${WRAP_MID}`;
const STATIC_WRAPPER_END = WRAP_SUFFIX;

function splitHtmlByTables(html: string): HtmlChunk[] {
  const containerWrapped = `<div id="__pdf_container__">${html}</div>`;
  const loadFn = cheerio.load as any;
  const $ = loadFn(containerWrapped);

  const PLACEHOLDER = "__PDF_TABLE_PLACEHOLDER__";
  const tableHtmls: string[] = [];

  // Collect table HTML and replace with placeholders in one pass
  $("table").each((_: number, el: cheerio.Element) => {
    const tableHtml = $.html(el) || "";
    tableHtmls.push(tableHtml);
    $(el).replaceWith(`<div>${PLACEHOLDER}${tableHtmls.length - 1}</div>`);
  });

  const flattened = $("#__pdf_container__").html() || "";

  // Early return if no tables found
  if (tableHtmls.length === 0) {
    return flattened.trim().length > 0
      ? [{ kind: "html", html: flattened }]
      : [];
  }

  const chunks: HtmlChunk[] = [];
  let cursor = 0;

  // Optimize placeholder search with a pre-compiled regex
  const placeholderRegex = new RegExp(`${PLACEHOLDER}(\\d+)`, "g");
  let match;

  // Reset regex lastIndex for consistent behavior
  placeholderRegex.lastIndex = 0;

  while ((match = placeholderRegex.exec(flattened)) !== null) {
    const matchStart = match.index;
    const matchEnd = placeholderRegex.lastIndex;

    // Add HTML content before this table placeholder
    if (matchStart > cursor) {
      const beforeHtml = flattened.slice(cursor, matchStart);
      if (beforeHtml.trim().length > 0) {
        chunks.push({ kind: "html", html: beforeHtml });
      }
    }

    // Add the table
    const tableIndex = parseInt(match[1], 10);
    const tableHtml = tableHtmls[tableIndex];
    if (tableHtml) {
      chunks.push({ kind: "table", html: tableHtml });
    }

    cursor = matchEnd;
  }

  // Add any remaining HTML content
  if (cursor < flattened.length) {
    const rest = flattened.slice(cursor);
    if (rest.trim().length > 0) {
      chunks.push({ kind: "html", html: rest });
    }
  }

  return chunks;
}

function PdfDocumentHtmlWithTableAdjustments({
  bodyHtml
}: PdfDocumentHtmlProps) {
  const chunks = useMemo(() => {
    if (!bodyHtml) return [] as HtmlChunk[];
    const normalized = normalizePrintableHtml(bodyHtml);
    return splitHtmlByTables(normalized);
  }, [bodyHtml]);

  if (!bodyHtml || chunks.length === 0) {
    const content = bodyHtml
      ? `${STATIC_WRAPPER_START}${normalizePrintableHtml(bodyHtml)}${STATIC_WRAPPER_END}`
      : "";

    return <Html>{content}</Html>;
  }

  return (
    <>
      {chunks.map((chunk, idx) => {
        if (chunk.kind === "table") {
          const desc = buildTableDescriptionFromHtml(chunk.html, {
            containerWidthPx: 430
          });
          if (!desc) return null;
          return <PdfDocumentTable key={idx} description={desc} />;
        }
        const wrapped = `${STATIC_WRAPPER_START}${chunk.html}${STATIC_WRAPPER_END}`;
        return <Html key={idx}>{wrapped}</Html>;
      })}
    </>
  );
}

// Memoize component to prevent unnecessary re-renders
export default memo(PdfDocumentHtmlWithTableAdjustments);
